import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { MenuItem, TextField } from '@mui/material';
import Grid from '@mui/material/Grid';
import UserApiService from '../../../api/UserApiService';
import AppUserGenericDTO from '../../../DTOs/User/AppUserGenericDTO';
import AppDialog from '../AppDialog';
import { usStates } from '../../../utilities/Constants';
import { formatPhoneNumber, isTextablePhoneNumber } from '../../../utilities/PhoneFormat';
import { getLicenseWarning } from '../../../utilities/DriversLicenseFormat';

const EMPTY_FORM = {
    id: 0,
    email: '',
    firstname: '',
    lastname: '',
    phone: '',
    driversLicense: '',
    licenseState: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipcode: '',
};

const REQUIRED_FIELDS = {
    firstname: 'First Name is required',
    lastname: 'Last Name is required',
    phone: 'Phone # is required',
    driversLicense: 'Driver\'s License is required',
    licenseState: 'License State is required',
};

export default function EditUserModal(props) {
    const { onClose, open, userInfo, setSnackbarProperties, userApiService, onCancel } = props;

    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    function formatPhoneOnBlur() {
        setForm((current) => ({...current, phone: formatPhoneNumber(current.phone)}));
    }

    /**
     * Warns about a number that cannot be turned into something Twilio will accept.
     *
     * Deliberately a warning rather than a validation error: the phone field is free text and this
     * form must not start refusing numbers it used to accept. But a tenant whose number cannot be
     * normalised can still opt in to texts and then silently never receive one, and this is the only
     * point at which anyone would notice.
     */
    function phoneWarning() {
        if (errors.phone || !form.phone?.trim() || isTextablePhoneNumber(form.phone)) {
            return '';
        }

        return 'This does not look like a number we can text. Include the area code, or a + and country code.';
    }

    /**
     * Warns when the licence number does not look like one the chosen state issues.
     *
     * A warning rather than a validation error on purpose: state formats change, and refusing a real
     * licence because this list has gone stale is worse than accepting a typo.
     */
    function licenseWarning() {
        if (errors.driversLicense) {
            return '';
        }

        return getLicenseWarning(form.driversLicense, form.licenseState);
    }

    function setField(name, value) {
        setForm((current) => ({...current, [name]: value}));

        // clear the error as soon as the user starts fixing the field
        setErrors((current) => current[name] ? {...current, [name]: undefined} : current);
    }

    /**
     * @return {Object<string, string>} field name -> message, empty when the form is valid
     */
    function validate() {
        const found = {};

        Object.entries(REQUIRED_FIELDS).forEach(([field, message]) => {
            if(!form[field]?.trim()) {
                found[field] = message;
            }
        });

        return found;
    }

    async function handleSave() {
        const found = validate();
        setErrors(found);

        if(Object.keys(found).length > 0) {
            return;
        }

        const newUser = new AppUserGenericDTO();
        newUser.id = form.id;
        newUser.username = form.email;
        newUser.firstName = form.firstname;
        newUser.lastName = form.lastname;
        newUser.phone = form.phone;
        newUser.driversLicenseNumber = form.driversLicense;
        newUser.licenseState = form.licenseState;
        newUser.address1 = form.address1;
        newUser.address2 = form.address2;
        newUser.city = form.city;
        newUser.state = form.state;
        newUser.zip = form.zipcode;

        setSaving(true);
        const response = await userApiService.updateUserInfo(newUser);
        setSaving(false);

        if(response.success) {
            onClose();
        } else {
            setSnackbarProperties(`Unable to update information: ${response.message}`, 'error');
        }
    }

    useEffect(() => {
        if(open) {
            setForm({
                id: userInfo?.id ?? 0,
                email: userInfo?.username ?? '',
                firstname: userInfo?.firstName ?? '',
                lastname: userInfo?.lastName ?? '',
                phone: formatPhoneNumber(userInfo?.phone ?? ''),
                driversLicense: userInfo?.driversLicenseNumber ?? '',
                licenseState: userInfo?.licenseState ?? '',
                address1: userInfo?.address1 ?? '',
                address2: userInfo?.address2 ?? '',
                city: userInfo?.city ?? '',
                state: userInfo?.state ?? '',
                zipcode: userInfo?.zip ?? '',
            });
            setErrors({});
        }
    }, [userInfo, open]);

    /**
     * @param name {string} key in the form state
     * @param label {string}
     * @param grid {Object} responsive column widths
     */
    function field(name, label, grid = { xs: 12, sm: 6 }, extraProps = {}) {
        return (
            <Grid item {...grid}>
                <TextField
                    fullWidth
                    id={name}
                    label={label}
                    required={!!REQUIRED_FIELDS[name]}
                    value={form[name]}
                    error={!!errors[name]}
                    helperText={errors[name] ?? ''}
                    onChange={(event) => setField(name, event.target.value)}
                    {...extraProps}
                />
            </Grid>
        );
    }

    /**
     * A state field, as a list rather than free text.
     *
     * Typed states arrive as "CO", "Colorado", "co" and the occasional typo, which makes them
     * useless to filter or report on. A blank option is offered because the address state is not a
     * required field and a tenant may have been created without one.
     * @param name {string} key in the form state
     * @param label {string}
     * @param grid {Object} responsive column widths
     */
    function stateField(name, label, grid = { xs: 12, sm: 6 }) {
        return (
            <Grid item {...grid}>
                <TextField
                    select
                    fullWidth
                    id={name}
                    label={label}
                    required={!!REQUIRED_FIELDS[name]}
                    value={usStates.includes(form[name]) ? form[name] : ''}
                    error={!!errors[name]}
                    helperText={errors[name] ?? ''}
                    onChange={(event) => setField(name, event.target.value)}
                >
                    {!REQUIRED_FIELDS[name] && <MenuItem value={''}><em>Not set</em></MenuItem>}
                    {usStates.map((state) => <MenuItem key={state} value={state}>{state}</MenuItem>)}
                </TextField>
            </Grid>
        );
    }

    return(
        <AppDialog
            open={open}
            onClose={onCancel}
            title={userInfo ? `Edit ${userInfo.username}` : 'Add New User'}
            onSubmit={handleSave}
            submitText='Save'
            busy={saving}
            maxWidth='md'
        >
            <Grid container spacing={2}>
                {field('firstname', 'First Name')}
                {field('lastname', 'Last Name')}
                {field('phone', 'Phone', { xs: 12, sm: 6 }, {
                    onBlur: formatPhoneOnBlur,
                    helperText: errors.phone ?? phoneWarning(),
                })}
                {field('driversLicense', 'Driver\'s License', { xs: 12, sm: 6 }, {
                    helperText: errors.driversLicense ?? licenseWarning(),
                })}
                {stateField('licenseState', 'License State')}
                {field('address1', 'Address Line 1', { xs: 12 })}
                {field('address2', 'Address Line 2', { xs: 12 })}
                {field('city', 'City', { xs: 12, sm: 6 })}
                {stateField('state', 'State', { xs: 6, sm: 3 })}
                {field('zipcode', 'Zip Code', { xs: 6, sm: 3 })}
            </Grid>
        </AppDialog>
    );
}

EditUserModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    userInfo: PropTypes.shape({
        id: PropTypes.number,
        username: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        phone: PropTypes.string,
        driversLicenseNumber: PropTypes.string,
        licenseState: PropTypes.string,
        address1: PropTypes.string,
        address2: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        zip: PropTypes.string,
    }),
    setSnackbarProperties: PropTypes.func,
    userApiService: PropTypes.instanceOf(UserApiService).isRequired,
    onCancel: PropTypes.func.isRequired,
};